// Uploads a list of files to S3 using the presigned POSTs returned by
// /librarian/bucket/get-upload-url. `files[i]` is paired with
// `presignedData[i]` by index, so callers must build both lists in the
// same order.
//
// Each file is retried up to `maxAttempts` times with the per-attempt
// delays in `retryDelaysMs` (length must be at least maxAttempts-1; only
// the first maxAttempts-1 entries are read). Both non-2xx responses and
// thrown network errors are retried.
//
// Returns the array of files whose every attempt failed. An empty array
// means every file succeeded. Translation of failure messages is left to
// the caller — this helper deals in plain File objects so it can be used
// outside of i18n-aware contexts (e.g. the help repository update flow).

const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_RETRY_DELAYS_MS = [500, 1500]

function buildFormData(file, presigned) {
  const formData = new FormData()
  Object.keys(presigned.fields).forEach((key) => {
    formData.append(key, presigned.fields[key])
  })
  formData.append("file", file)
  return formData
}

async function uploadOneWithRetry(file, presigned, maxAttempts, retryDelaysMs) {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    let succeeded = false
    try {
      const response = await fetch(presigned.url, {
        method: "POST",
        body: buildFormData(file, presigned),
      })
      succeeded = response.ok
    } catch {
      succeeded = false
    }
    if (succeeded) return true
    if (attempt < maxAttempts) {
      const delay = retryDelaysMs[attempt - 1] ?? retryDelaysMs[retryDelaysMs.length - 1] ?? 0
      await new Promise((resolve) => setTimeout(resolve, delay))
    }
  }
  return false
}

export async function uploadFilesToS3(
  files,
  presignedData,
  { onProgress, maxAttempts = DEFAULT_MAX_ATTEMPTS, retryDelaysMs = DEFAULT_RETRY_DELAYS_MS } = {}
) {
  const failed = []
  for (let i = 0; i < files.length; i++) {
    const ok = await uploadOneWithRetry(files[i], presignedData[i], maxAttempts, retryDelaysMs)
    if (!ok) failed.push(files[i])
    if (onProgress) onProgress(Math.round(((i + 1) / files.length) * 100))
  }
  return failed
}
